import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./ContratoMensalPage.module.css";
import { wf1Api } from "../wf1/api";
import { getContratoMensalPlano, atualizarContratoMensalLinha } from "../planejamentomensal/api";

const WEEK_IDS = [1, 2, 3, 4, 5];
const pad2 = (n) => String(n).padStart(2, "0");
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

function monthLabel(value) {
  if (!value) return "";
  const [y, m] = value.split("-");
  if (!y || !m) return value;
  try {
    const dt = new Date(Number(y), Number(m) - 1, 1);
    const label = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(dt);
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return value;
  }
}

function readQueryMonth(search) {
  const params = new URLSearchParams(search || "");
  const value = String(params.get("month") || "").trim();
  return /^\d{4}-\d{2}$/.test(value) ? value : "";
}

function parseTempoToMinutes(raw) {
  const str = String(raw || "").trim();
  if (!str) return 0;
  const match = str.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (!match) return 0;
  const hours = Number(match[1]) || 0;
  const minutes = Number(match[2]) || 0;
  return Math.max(0, hours * 60 + minutes);
}

function formatMinutes(totalMinutes) {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

function normalizeTempoHHMM(raw) {
  const str = String(raw || "").trim();
  if (!str) return "";
  const match = str.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (!match) return "";
  const hours = Math.max(0, Number(match[1]) || 0);
  const minutes = Math.min(59, Math.max(0, Number(match[2]) || 0));
  return `${pad2(hours)}:${pad2(minutes)}`;
}

function normalizeTimeTyping(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function normalizeTimeBlur(raw) {
  const str = String(raw || "").trim();
  if (!str) return "";
  return formatMinutes(parseTempoToMinutes(str));
}

function normalizeQtyTyping(raw) {
  return String(raw || "").replace(/\D/g, "").slice(0, 8);
}

function rowKey(row) {
  return JSON.stringify([row.gerencia || "", row.coordenador || "", row.atividade || "", row.unidade || ""]);
}

function buildDraftFromRow(row) {
  const draft = {};
  for (const week of WEEK_IDS) {
    const sem = row?.semanas?.[week];
    draft[week] = {
      quantidade: sem?.quantidade != null && sem?.quantidade !== "" ? String(sem.quantidade) : "",
      tempo: sem?.tempo ? normalizeTempoHHMM(sem.tempo) : "",
    };
  }
  return draft;
}

function noKey(gerencia, coordenador) {
  return `${String(gerencia || "").trim()}__${String(coordenador || "").trim()}`;
}

function normalizeNos(rawNos) {
  if (!Array.isArray(rawNos)) return [];
  return Array.from(new Set(rawNos.map((n) => String(n || "").trim()).filter(Boolean)));
}

export default function ContratoMensalPage({ onBack }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [month, setMonth] = useState(() => readQueryMonth(location.search) || currentMonth());
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [rows, setRows] = useState([]);
  const [busyRow, setBusyRow] = useState("");
  const [editingRow, setEditingRow] = useState("");
  const [rowDrafts, setRowDrafts] = useState({});
  const [nosByCoord, setNosByCoord] = useState({});

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const out = await getContratoMensalPlano({ month });
      setRows(Array.isArray(out?.rows) ? out.rows : []);
    } catch {
      setError("Falha ao carregar os dados da aprovacao do contrato.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    const fromQuery = readQueryMonth(location.search);
    if (fromQuery && fromQuery !== month) {
      setMonth(fromQuery);
    }
  }, [location.search, month]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setEditingRow("");
    setRowDrafts({});
    setFeedback("");
  }, [month]);

  useEffect(() => {
    let alive = true;
    const gerencias = Array.from(
      new Set((rows || []).map((r) => String(r?.gerencia || "").trim()).filter(Boolean))
    );

    if (!gerencias.length) {
      setNosByCoord({});
      return () => {
        alive = false;
      };
    }

    Promise.all(
      gerencias.map(async (gerencia) => {
        try {
          const res = await wf1Api.getCoordenadores(gerencia, { detalhado: true });
          return { gerencia, data: Array.isArray(res?.data?.data) ? res.data.data : [] };
        } catch {
          return { gerencia, data: [] };
        }
      })
    ).then((all) => {
      if (!alive) return;
      const next = {};
      for (const block of all) {
        const gerencia = String(block?.gerencia || "").trim();
        for (const item of block?.data || []) {
          const coordenador = String(item?.coordenador_nome || item?.coordenador || item?.nome || "").trim();
          if (!gerencia || !coordenador) continue;
          next[noKey(gerencia, coordenador)] = normalizeNos(item?.nos);
        }
      }
      setNosByCoord(next);
    });

    return () => {
      alive = false;
    };
  }, [rows]);

  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    navigate("/");
  };

  const goToAjuste = () => {
    const qs = new URLSearchParams({ month }).toString();
    navigate(`/programacoes/planejamento-mensal/ajuste?${qs}`);
  };

  const contratoRows = useMemo(() => {
    const map = new Map();

    for (const row of rows || []) {
      const semana = Number(row?.semana);
      if (!WEEK_IDS.includes(semana)) continue;

      const gerencia = String(row?.gerencia || "").trim();
      const coordenador = String(row?.coordenador || "").trim();
      const atividade = String(row?.atividade || "").trim();
      const grupo = String(row?.grupo || "").trim();
      const unidade = String(row?.unidade || "").trim();
      if (!coordenador || !atividade) continue;

      const key = JSON.stringify([gerencia, coordenador, atividade, grupo, unidade]);
      if (!map.has(key)) {
        map.set(key, {
          gerencia,
          coordenador,
          atividade,
          grupo,
          unidade,
          contrato_status: "PENDENTE",
          semanas: {},
        });
      }

      const item = map.get(key);
      item.contrato_status = String(row?.contrato_status || item.contrato_status || "PENDENTE").trim().toUpperCase();
      item.semanas[semana] = {
        quantidade:
          row?.quantidade == null || row?.quantidade === "" ? "" : String(Number(row?.quantidade) || 0),
        tempo: normalizeTempoHHMM(row?.tempo || row?.tempo_min || ""),
      };
    }

    const list = Array.from(map.values()).map((item) => {
      let quantidadeTotal = 0;
      let tempoTotalMin = 0;

      for (const week of WEEK_IDS) {
        const sem = item.semanas[week] || { quantidade: "", tempo: "" };
        const quantidade = Number(sem.quantidade || 0);
        quantidadeTotal += quantidade;
        tempoTotalMin += quantidade * parseTempoToMinutes(sem.tempo);
      }

      const nos = nosByCoord[noKey(item.gerencia, item.coordenador)] || [];
      const noLabel = nos.length ? nos.join(", ") : "SEM NO";
      const status = String(item.contrato_status || "PENDENTE").toUpperCase();

      return {
        ...item,
        nos,
        noLabel,
        contrato_status: status,
        isAceita: status === "ACEITO",
        isNegada: status === "NEGADO",
        quantidadeTotal,
        tempoTotalMin,
      };
    });

    return list.sort((a, b) => {
      const g = String(a.gerencia).localeCompare(String(b.gerencia), "pt-BR");
      if (g) return g;
      const n = String(a.noLabel).localeCompare(String(b.noLabel), "pt-BR");
      if (n) return n;
      return String(a.atividade).localeCompare(String(b.atividade), "pt-BR");
    });
  }, [rows, nosByCoord]);

  const filteredRows = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return contratoRows;
    return contratoRows.filter((row) => {
      const hay = `${row.gerencia} ${row.noLabel} ${row.coordenador} ${row.atividade} ${row.grupo} ${row.unidade}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contratoRows, query]);

  const groupedRows = useMemo(() => {
    const out = [];
    let i = 0;
    while (i < filteredRows.length) {
      const row = filteredRows[i];
      const groupKey = `${String(row?.gerencia || "").trim()}__${String(row?.noLabel || "").trim()}`;

      let j = i + 1;
      while (j < filteredRows.length) {
        const next = filteredRows[j];
        const nextKey = `${String(next?.gerencia || "").trim()}__${String(next?.noLabel || "").trim()}`;
        if (nextKey !== groupKey) break;
        j += 1;
      }

      const span = j - i;
      for (let k = i; k < j; k += 1) {
        out.push({
          ...filteredRows[k],
          __showGroupCells: k === i,
          __groupRowSpan: span,
        });
      }
      i = j;
    }
    return out;
  }, [filteredRows]);

  const resumo = useMemo(() => {
    let quantidadeTotal = 0;
    let tempoTotalMin = 0;

    for (const row of filteredRows) {
      quantidadeTotal += row.quantidadeTotal || 0;
      tempoTotalMin += row.tempoTotalMin || 0;
    }

    return {
      linhas: filteredRows.length,
      aceitas: filteredRows.filter((r) => r.isAceita).length,
      negadas: filteredRows.filter((r) => r.isNegada).length,
      pendentes: filteredRows.filter((r) => !r.isAceita && !r.isNegada).length,
      quantidadeTotal,
      tempoTotalMin,
    };
  }, [filteredRows]);

  const updateDraftValue = (key, semana, field, value) => {
    setRowDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [semana]: {
          ...((prev[key] || {})[semana] || { quantidade: "", tempo: "" }),
          [field]: value,
        },
      },
    }));
  };

  const handleAtualizarStatus = async (row, nextStatus) => {
    const key = rowKey(row);
    if (!month || busyRow) return;

    setBusyRow(key);
    setError("");
    setFeedback("");
    try {
      const out = await atualizarContratoMensalLinha({
        month,
        gerencia: row.gerencia,
        coordenador: row.coordenador,
        atividade: row.atividade,
        unidade: row.unidade || null,
        grupo: row.grupo || null,
        contrato_status: nextStatus,
        semanas: [],
      });

      if (!out?.ok) {
        setError(out?.message || "Falha ao atualizar o status do contrato.");
        return;
      }

      if (editingRow === key) setEditingRow("");
      if (nextStatus === "ACEITO") setFeedback("Linha aceita no contrato mensal.");
      else if (nextStatus === "NEGADO") setFeedback("Linha negada no contrato mensal.");
      else setFeedback("Linha reaberta no contrato mensal.");

      await loadRows();
    } catch {
      setError("Falha ao atualizar o status do contrato.");
    } finally {
      setBusyRow("");
    }
  };

  const handleStartEdit = (row) => {
    const key = rowKey(row);
    if (row.isAceita) return;
    setEditingRow(key);
    setError("");
    setFeedback("");
    setRowDrafts((prev) => ({
      ...prev,
      [key]: prev[key] || buildDraftFromRow(row),
    }));
  };

  const handleCancelEdit = () => {
    setEditingRow("");
    setError("");
  };

  const handleSalvarEdicao = async (row) => {
    const key = rowKey(row);
    if (!month || busyRow) return;

    const draft = rowDrafts[key] || buildDraftFromRow(row);
    const semanas = WEEK_IDS.map((semana) => {
      const sem = draft?.[semana] || { quantidade: "", tempo: "" };
      return {
        semana,
        quantidade: String(sem.quantidade || "").trim(),
        tempo: normalizeTimeBlur(sem.tempo || ""),
      };
    });

    setBusyRow(key);
    setError("");
    setFeedback("");
    try {
      const out = await atualizarContratoMensalLinha({
        month,
        gerencia: row.gerencia,
        coordenador: row.coordenador,
        atividade: row.atividade,
        unidade: row.unidade || null,
        grupo: row.grupo || null,
        contrato_status: "PENDENTE",
        semanas,
      });

      if (!out?.ok) {
        setError(out?.message || "Falha ao salvar a edicao do contrato.");
        return;
      }

      setEditingRow("");
      setFeedback("Edicao salva no contrato mensal.");
      await loadRows();
    } catch {
      setError("Falha ao salvar a edicao do contrato.");
    } finally {
      setBusyRow("");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <button className={styles.backBtn} type="button" onClick={goBack}>
            Voltar ao menu principal
          </button>
          <button className={styles.ajusteBtn} type="button" onClick={goToAjuste}>
            Voltar para ajuste mensal
          </button>

          <div className={styles.card}>
            <h1>Aprovacao do contrato mensal</h1>
            <p>Etapa final: aceite, negacao ou edicao das linhas por NO.</p>
          </div>

          <div className={styles.card}>
            <label className={styles.field}>
              <span>Mes de referencia</span>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>Buscar</span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Gerencia, NO ou atividade"
              />
            </label>
          </div>

          <div className={styles.stats}>
            <div className={styles.stat}>
              <span>Competencia</span>
              <strong>{monthLabel(month)}</strong>
            </div>
            <div className={styles.stat}>
              <span>Linhas</span>
              <strong>{resumo.linhas}</strong>
            </div>
            <div className={styles.stat}>
              <span>Pendentes</span>
              <strong>{resumo.pendentes}</strong>
            </div>
            <div className={styles.stat}>
              <span>Aceitas</span>
              <strong>{resumo.aceitas}</strong>
            </div>
            <div className={styles.stat}>
              <span>Negadas</span>
              <strong>{resumo.negadas}</strong>
            </div>
            <div className={styles.stat}>
              <span>Quantidade total</span>
              <strong>{resumo.quantidadeTotal}</strong>
            </div>
            <div className={styles.stat}>
              <span>Horas totais (Qtd x Hora)</span>
              <strong>{formatMinutes(resumo.tempoTotalMin)}</strong>
            </div>
          </div>
        </aside>

        <main className={styles.main}>
          <header className={styles.mainHead}>
            <h2>Contrato mensal</h2>
            <p>Aceite, negue ou edite as linhas finais. O agrupamento visual esta por NO.</p>
          </header>

          <section className={styles.panel}>
            {loading ? <div className={styles.feedback}>Carregando dados do contrato mensal...</div> : null}
            {error ? <div className={`${styles.feedback} ${styles.feedbackError}`}>{error}</div> : null}
            {feedback ? <div className={`${styles.feedback} ${styles.feedbackOk}`}>{feedback}</div> : null}
            {!loading && !error && !filteredRows.length ? (
              <div className={styles.feedback}>Nenhum dado encontrado para os filtros informados.</div>
            ) : null}

            {!!filteredRows.length && (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Gerencia</th>
                      <th>NO</th>
                      <th>Coordenador</th>
                      <th>Atividade</th>
                      <th>Unidade</th>
                      {WEEK_IDS.map((week) => (
                        <th key={week}>Sem {week}</th>
                      ))}
                      <th>Total</th>
                      <th>Status contrato</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>

                  <tbody>
                    {groupedRows.map((row, idx) => {
                      const key = rowKey(row);
                      const isEditing = editingRow === key && !row.isAceita;
                      const draft = rowDrafts[key] || buildDraftFromRow(row);

                      return (
                        <tr
                          key={`${row.gerencia}__${row.coordenador}__${row.atividade}__${row.unidade}__${idx}`}
                          className={`${row.isAceita ? styles.rowAceita : ""} ${row.isNegada ? styles.rowNegada : ""}`}
                        >
                          {row.__showGroupCells ? (
                            <td rowSpan={row.__groupRowSpan} className={styles.groupCell}>
                              {row.gerencia || "-"}
                            </td>
                          ) : null}
                          {row.__showGroupCells ? (
                            <td rowSpan={row.__groupRowSpan} className={styles.groupCell}>
                              <div className={styles.noCell}>
                                <strong>{row.noLabel || "SEM NO"}</strong>
                              </div>
                            </td>
                          ) : null}
                          <td>{row.coordenador || "-"}</td>
                          <td>{row.atividade || "-"}</td>
                          <td>{row.unidade || "-"}</td>

                          {WEEK_IDS.map((week) => {
                            const semDraft = draft?.[week] || { quantidade: "", tempo: "" };
                            const sem = row.semanas[week];

                            if (!sem && !isEditing) return <td key={week} className={styles.weekCellEmpty}>-</td>;

                            if (isEditing) {
                              return (
                                <td key={week}>
                                  <div className={styles.cellInputs}>
                                    <input
                                      className={styles.cellInput}
                                      type="text"
                                      inputMode="numeric"
                                      pattern="\d*"
                                      value={semDraft.quantidade}
                                      placeholder="Quantidade"
                                      onChange={(e) =>
                                        updateDraftValue(key, week, "quantidade", normalizeQtyTyping(e.target.value))
                                      }
                                    />
                                    <input
                                      className={styles.cellInput}
                                      type="text"
                                      inputMode="numeric"
                                      maxLength={5}
                                      value={semDraft.tempo}
                                      placeholder="hh:mm"
                                      onChange={(e) =>
                                        updateDraftValue(key, week, "tempo", normalizeTimeTyping(e.target.value))
                                      }
                                      onBlur={() =>
                                        updateDraftValue(key, week, "tempo", normalizeTimeBlur(semDraft.tempo))
                                      }
                                    />
                                  </div>
                                </td>
                              );
                            }

                            return (
                              <td key={week}>
                                <div className={styles.weekCell}>
                                  <span>Qtd: {sem.quantidade === "" ? 0 : sem.quantidade}</span>
                                  <span>Hora: {sem.tempo || "00:00"}</span>
                                </div>
                              </td>
                            );
                          })}

                          <td>
                            <div className={styles.totalCell}>
                              <span>Qtd: {row.quantidadeTotal}</span>
                              <span>Hora: {formatMinutes(row.tempoTotalMin)}</span>
                            </div>
                          </td>

                          <td>
                            <span
                              className={`${styles.statusBadge} ${row.isAceita ? styles.statusAceito : ""} ${row.isNegada ? styles.statusNegado : ""}`}
                            >
                              {row.contrato_status || "PENDENTE"}
                            </span>
                          </td>

                          <td className={styles.actionsCell}>
                            <div className={styles.actionsStack}>
                              <button
                                type="button"
                                className={`${styles.actionBtn} ${styles.actionBtnSuccess}`}
                                disabled={busyRow === key}
                                onClick={() => handleAtualizarStatus(row, row.isAceita ? "PENDENTE" : "ACEITO")}
                              >
                                {row.isAceita ? "Reabrir" : "Aceitar"}
                              </button>

                              <button
                                type="button"
                                className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                                disabled={busyRow === key}
                                onClick={() => handleAtualizarStatus(row, row.isNegada ? "PENDENTE" : "NEGADO")}
                              >
                                {row.isNegada ? "Reabrir" : "Negar"}
                              </button>

                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    className={styles.actionBtn}
                                    disabled={busyRow === key}
                                    onClick={() => handleSalvarEdicao(row)}
                                  >
                                    Salvar edicao
                                  </button>
                                  <button
                                    type="button"
                                    className={`${styles.actionBtn} ${styles.actionBtnGhost}`}
                                    disabled={busyRow === key}
                                    onClick={handleCancelEdit}
                                  >
                                    Cancelar
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className={styles.actionBtn}
                                  disabled={busyRow === key || row.isAceita}
                                  onClick={() => handleStartEdit(row)}
                                >
                                  {row.isAceita ? "Aceito" : "Editar"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
